import React, { CSSProperties, useContext } from 'react';
import { AxiosResponse } from 'axios';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { t } from 'i18next';

import Settings from '../../data/settings';
import { uderlyConfig } from "../../";
import { uploadFile, errorLog } from '../../api/';
import { DataLoader, LayoutContext, ILayoutContext } from '../';

export interface MediaImageProps {
  imageUrl?: string | null;
  item?: any;
  media?: [];
  apiPath?: string;
  disabled?: boolean;
  viewOnly?: boolean;
  variant?: string;
  className?: string;
  imageSx?: CSSProperties;
  onImageFileChanged?: (file: File) => void;
  onImageRemoved?: (media: any) => void;
}

class MediaImage extends React.Component<MediaImageProps & { layoutContext: ILayoutContext }> {
  state = {
    status: 0,
    imageUrl: this.props.imageUrl,
    image: null,
    drag: "",
    item: this.props.item,
    media: this.props.media ? this.props.media :
      (this.props.item && this.props.item.media && this.props.item.media.length > 0) ? this.props.item.media[0] : null
  };

  containerRef = React.createRef<HTMLDivElement>();
  fileInputRef = React.createRef<HTMLInputElement>();

  get apiPath() {
    return this.props.apiPath ? this.props.apiPath : '/mediastock';
  }

  async componentDidMount() {
    const div = this.containerRef.current;

    if (div) {
      div.addEventListener('dragenter', this.onDragEnter);
      div.addEventListener('dragleave', this.onDragLeave);
      div.addEventListener('dragover', this.onDragOver);
      div.addEventListener('drop', this.onDrop);

      if (this.state.image === null && this.state.imageUrl !== null) {
        await this.fetchImage();
      }
    }
  }

  componentDidUpdate = async (prevProps: MediaImageProps) => {
    if (this.props.imageUrl !== prevProps.imageUrl) {
      await this.fetchImage();
    }
  }

  componentWillUnmount() {
    if (this.containerRef) {
      const div = this.containerRef.current;

      if (div) {
        div.removeEventListener('dragenter', this.onDragEnter);
        div.removeEventListener('dragleave', this.onDragLeave);
        div.removeEventListener('dragover', this.onDragOver);
        div.removeEventListener('drop', this.onDrop);
      }
    }
  }

  onDragEnter = (event: DragEvent) => {
    event.preventDefault();

    if (this.state.drag !== "enter")
      this.setState({
        drag: "enter"
      });
  }

  onDragOver = (event: DragEvent) => {
    event.preventDefault();

    const { drag, status } = this.state;

    if (status === 0 && drag !== "over")
      this.setState({
        drag: "over"
      });

    // if (event.dataTransfer != null && event.dataTransfer.files != null)
    //     console.log(event.dataTransfer, event.dataTransfer.files);
  }

  onDragLeave = (event: DragEvent) => {
    event.preventDefault();

    const { drag, status } = this.state;
    console.log("onDragLeave", event)

    if (status === 0 && drag !== "leave")
      this.setState({
        drag: "leave"
      });
  }

  onDrop = async (event: DragEvent) => {
    event.preventDefault();

    const { drag, status } = this.state;
    console.log("onDrop", event);

    if (status === 0 && drag !== "drop")
      this.setState({
        drag: "drop"
      });

    let file: File | null | undefined = null;

    if (event.dataTransfer) {
      if (event.dataTransfer.items) {
        // Use DataTransferItemList interface to access the file(s)
        for (let i = 0; i < event.dataTransfer.items.length; i++) {
          // If dropped items aren't files, reject them
          if (event.dataTransfer.items[i]?.kind === 'file') {
            file = event.dataTransfer.items[i]?.getAsFile();
            if (file)
              console.log('... file[' + i + '].name = ' + file.name);
          }
        }
      } else {
        // Use DataTransfer interface to access the file(s)
        for (let i = 0; i < event.dataTransfer.files.length; i++) {
          if (event.dataTransfer.files[i])
            console.log('... file[' + i + '].name = ' + event.dataTransfer.files[i]!.name);
        }
      }
    }

    if (file)
      await this.onFileChanged([file])
  }

  onFileChanged = async (files: FileList | null | File[]) => {
    if (files && files[0]) {
      this.setState({
        status: 1
      });

      const reader = new FileReader();

      reader.onload = (files) => {
        if (files.target)
          this.setState({
            image: files.target.result
          });
      };

      reader.readAsDataURL(files[0]);

      const formData = new FormData();
      formData.append('files', files[0]);

      const url = Settings.apiUrl + this.apiPath + "/upload";
      const response = await uploadFile(url, formData);

      console.log(response);

      if (this.props.onImageFileChanged)
        this.props.onImageFileChanged(files[0]);

      this.setState({
        status: 0
      });
    }
  }

  openFileDialog = () => {
    if (this.state.status !== 0 || this.props.disabled || this.props.viewOnly) return;

    this.fileInputRef.current?.click();
  }

  onDelete = async (event: React.MouseEvent<HTMLElement> | undefined) => {
    event?.stopPropagation();

    const { media } = this.state;

    let r: null | AxiosResponse<any, any> = null; // Delete Response

    if (this.state.item.id) {
      let url = Settings.apiUrl + this.apiPath + "/" + this.state.item.id + "/image";
      if (media && media.collection_name)
        url += "?collectionName=" + media.collection_name;

      r = await uderlyConfig.Axios?.delete(url)
        .catch(function (error) {
          console.log('error ' + error);
          errorLog(error);

          return error.response;
        });

      if (this.props.onImageRemoved)
        this.props.onImageRemoved(media);

      this.setState({
        status: 0
      });
    }

    if (!this.state.item.id || (r && r.status === 200)) {
      if (this.fileInputRef.current)
        this.fileInputRef.current.value = "";

      this.setState({
        status: 0,
        image: null,
        imageUrl: null
      });
    }
  }

  onView = (event: React.MouseEvent<HTMLElement> | undefined) => {
    event?.stopPropagation();

    const { media, image } = this.state;

    const title = media ? media.name : "";

    if (image)
      this.props.layoutContext.dialog.show({ title: "", content: <img src={image} alt={title} /> });
  }

  async fetchBase64Image(url: string) {
    const response = await uderlyConfig.Axios?.get(url, { responseType: 'arraybuffer' })
      .catch((error) => {
        console.log('error ' + error);
        errorLog(error);
      });

    if (response && response.status === 200) {
      const image = btoa(
        new Uint8Array(response.data)
          .reduce((data, byte) => data + String.fromCharCode(byte), '')
      );

      if (image !== null && image.length > 0)
        return `data:${response.headers['content-type'].toLowerCase()};base64,${image}`;
    }

    return null;
  }

  fetchImage = async () => {
    const { imageUrl } = this.props;

    if (imageUrl !== "") {
      this.setState({
        status: 1,
        image: null
      });

      if (imageUrl) {
        const imageBase64 = await this.fetchBase64Image(imageUrl);

        this.setState({
          imageUrl: this.props.imageUrl,
          image: imageBase64,
          status: 0
        });
      }
    }
  }

  render() {
    const { variant, viewOnly } = this.props;
    const { drag, image, status } = this.state;

    const acceptFiles = "image/gif, image/jpeg, image/png";
    const className = (this.props.className ? this.props.className : "") + " media-image "
      + (variant ? variant : "") + (drag ? (" drag-" + drag) : "") + ((status === 1) ? " loading" : "");

    // let ratio = 1;
    // if(variant && variant.substr(0, "ratio".length) === "ratio")
    //     ratio = variant.substr("ratio".length, "variant".length);

    const imageStyle = { backgroundImage: `url(${this.state.image})`, ...this.props.imageSx };

    const options = (
      <div className="options">
        {!viewOnly &&
          <IconButton aria-label="delete" onClick={this.onDelete}>
            <DeleteIcon className="delete-button" />
          </IconButton>}

        <IconButton aria-label="view" onClick={this.onView}>
          <ImageIcon />
        </IconButton>
      </div>);

    return (
      <div ref={this.containerRef}
        className={className}
        style={this.props.imageSx ? this.props.imageSx : {}}
        onClick={this.openFileDialog}>
        {status === 0 && !viewOnly &&
          <div className="image" style={imageStyle}>
            <div className="browse">
              <AddCircleOutlineIcon />
            </div>
          </div>}

        {!image && viewOnly &&
          <div className="overlay-texts">
            <p>{t("image-instructions1")}</p>
            <p><small>{t("image-instructions2")}</small></p>
          </div>}

        {status !== 0 && <DataLoader fullscreen />}

        {(image && status === 0) && options}

        <input type="file" ref={this.fileInputRef} className="FileInput"
          accept={acceptFiles}
          onChange={(e) => { this.onFileChanged(e.target.files); }} />
      </div>);
  }
}

const MediaImageWrap = (props: MediaImageProps) => {
  const layoutContext = useContext(LayoutContext) as ILayoutContext;

  return <MediaImage {...props} layoutContext={layoutContext} />
}

export default MediaImageWrap;